import { Combobox, ComboboxEmptyState, type ComboboxProps, Input } from '@zillow/constellation';
import type { UseQueryResult } from '@zg-rentals/react-query5';
import type { ApiResponseDto } from '@zg-rentals/ts-rental-manager-api-client';
import { type AddressResponse, MIN_AUTOCOMPLETE_ADDRESS_LENGTH } from '../../hooks/useAddressAutocomplete';
import type { ReactNode } from 'react';

type AddressesResponse = {
  addresses: Array<AddressResponse>;
};

export interface AddressAutocompleteProps extends Omit<ComboboxProps, 'onChange' | 'options'> {
  /** The stored form field value (e.g., street address) */
  value: string;
  /** Current partial address input value for autocomplete search */
  partialAddress: string;
  /** Function to update the partial address (from useAddressAutocomplete hook) */
  onSetPartialAddress: (value: string) => void;
  /** Query result from useAddressAutocomplete hook */
  queryData: UseQueryResult<ApiResponseDto<AddressesResponse>, Error>;
  /** Callback when user selects an address from the dropdown */
  onAddressSelect: (address: AddressResponse) => void;
  /** Callback when user types in the input (for syncing with form state) */
  onInputChange?: (value: string) => void;
  disabled?: boolean;
  /** Whether the input should show error styling */
  error?: boolean;
  /** ID of the label element for accessibility */
  'aria-labelledby'?: string;
  renderEmptyState?: ReactNode;
}

export function AddressAutocomplete({
  value,
  partialAddress,
  onSetPartialAddress,
  queryData,
  onAddressSelect,
  onInputChange,
  renderEmptyState,
  disabled,
  error,
  'aria-labelledby': ariaLabelledby,
  onBlur,
  ...rest
}: AddressAutocompleteProps) {
  const displayValue = value || partialAddress;

  return (
    <Combobox
      {...rest}
      appearance="input"
      focusFirstOption={true}
      options={
        queryData?.data?.response?.addresses?.map((address) => ({
          ...address,
          value: address.name,
          label: address.name,
        })) ?? []
      }
      disabled={disabled}
      // @ts-expect-error - Combobox types don't properly support generic option types
      onOptionSelect={(selectedAddress: AddressResponse & { value: string; label: string }) => {
        onAddressSelect(selectedAddress);
        onSetPartialAddress('');
      }}
      loading={queryData.isFetching}
      renderInput={({ ...rest }) => {
        return (
          <Input
            {...rest}
            onBlur={(...args) => {
              // Constellation Input has a bug where if this is passed directly as onBlur={onBlur} an
              // "Cannot read properties of undefined (reading 'apply')" error is thrown
              onBlur?.(...args);
            }}
            value={displayValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onSetPartialAddress(e.target.value);
              onInputChange?.(e.target.value);
            }}
            aria-labelledby={ariaLabelledby}
            error={error}
          />
        );
      }}
      renderEmptyState={() =>
        partialAddress.length >= MIN_AUTOCOMPLETE_ADDRESS_LENGTH && queryData?.data ? (
          renderEmptyState ? (
            renderEmptyState
          ) : (
            <ComboboxEmptyState>No address found</ComboboxEmptyState>
          )
        ) : null
      }
    />
  );
}
